import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { CloudService } from '../../core/services/cloud.service';
import { ALL_REGIONS_SCOPE } from '../../core/aws-regions';

/**
 * Implemented by every Cloud service detail component so the shared page header
 * can drive a manual refresh (bypass cache + reload) without knowing the
 * service specifics.
 */
export interface RefreshableCloudDetail {
  refresh(): void;
  readonly refreshing: boolean;
}

/**
 * Full-page chrome for a single Cloud service detail view.
 *
 * Each `/cloud/:serviceType` route renders this wrapper with route `data`
 * ({ title, icon }) and the service-specific detail component as its child
 * route. The wrapper provides the page padding, a breadcrumb/back path to the
 * `/cloud` overview, the page title, a manual refresh action, and the bordered
 * content card — so the detail components stay focused on their tables/filters.
 */
@Component({
  selector: 'app-cloud-service-page',
  templateUrl: './cloud-service-page.component.html',
})
export class CloudServicePageComponent implements OnInit, OnDestroy {
  title = 'Service';
  icon  = 'cloud';

  /** Active AWS account + region scope, inherited from the ?account= / ?region= query params. */
  account = '';
  scope = 'us-east-1';
  enabledRegions: string[] = [];
  defaultRegion = 'us-east-1';
  private accounts: { accountId: string; defaultRegion: string; enabledRegions: string[] }[] = [];
  readonly ALL_REGIONS = ALL_REGIONS_SCOPE;

  private activeChild: RefreshableCloudDetail | null = null;

  private sub?: Subscription;
  private connSub?: Subscription;
  private qpSub?: Subscription;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cloud: CloudService,
  ) {}

  ngOnInit(): void {
    this.sub = this.route.data.subscribe(data => {
      this.title = (data['title'] as string) ?? 'Service';
      this.icon  = (data['icon']  as string) ?? 'cloud';
    });

    // Set account + scope synchronously from the URL so the child's first load
    // is correctly scoped (no flash of the wrong account/region).
    const a0 = this.route.snapshot.queryParamMap.get('account');
    const r0 = this.route.snapshot.queryParamMap.get('region');
    if (a0) { this.account = a0; this.cloud.setAccount(a0); }
    if (r0) { this.scope = r0; this.cloud.setScope(r0); }

    // Resolve the account list + the selected account's regions for the selector
    // and no-param deep links.
    this.connSub = this.cloud.getConnectionState().pipe(take(1)).subscribe(({ connection }) => {
      if (!connection) return;
      this.accounts = this.cloud.getAwsAccounts(connection);
      this.applyFromRoute();
    });

    // React to ?account= / ?region= changes (selector, deep links, back/forward).
    this.qpSub = this.route.queryParams.subscribe(() => this.applyFromRoute());
  }

  /** Resolves account (first) then region within it, and reloads the active detail on a real change. */
  private applyFromRoute(): void {
    const qpAccount = this.route.snapshot.queryParamMap.get('account');
    const account = (qpAccount && this.accounts.some(a => a.accountId === qpAccount))
      ? qpAccount
      : (this.accounts[0]?.accountId ?? this.cloud.getAccount());

    const acct = this.accounts.find(a => a.accountId === account);
    this.enabledRegions = acct?.enabledRegions ?? this.enabledRegions;
    this.defaultRegion  = acct?.defaultRegion ?? this.defaultRegion;

    const qpRegion = this.route.snapshot.queryParamMap.get('region');
    const region = (qpRegion && (qpRegion === this.ALL_REGIONS || this.enabledRegions.includes(qpRegion)))
      ? qpRegion
      : this.defaultRegion;

    const changed = account !== this.cloud.getAccount() || region !== this.cloud.getScope();
    this.account = account;
    this.scope   = region;
    this.cloud.setAccount(account);
    this.cloud.setScope(region);
    if (changed) this.activeChild?.refresh();   // reload the active detail under the new account/region
  }

  /** Region selector change → write the bookmarkable ?region= param (account preserved via merge). */
  onScopeChange(scope: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { region: scope }, queryParamsHandling: 'merge' });
  }

  get showRegionSwitcher(): boolean {
    return this.enabledRegions.length > 1;
  }

  /** Whether more than one AWS account is connected (drives the breadcrumb account chip). */
  get showAccount(): boolean {
    return this.accounts.length > 1;
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.connSub?.unsubscribe();
    this.qpSub?.unsubscribe();
  }

  /** Captures the routed detail component if it supports manual refresh. */
  onActivate(component: unknown): void {
    this.activeChild = isRefreshable(component) ? component : null;
  }

  onDeactivate(): void {
    this.activeChild = null;
  }

  get refreshing(): boolean {
    return this.activeChild?.refreshing ?? false;
  }

  refresh(): void {
    this.activeChild?.refresh();
  }
}

function isRefreshable(c: unknown): c is RefreshableCloudDetail {
  return !!c && typeof (c as RefreshableCloudDetail).refresh === 'function';
}
